import type { OfflineFavoriteEntry } from '~/composables/useOfflineFavorites';

import { fetchPropertySnapshotSource } from '~/composables/useBehaviorEventTracking/fetchPropertySnapshotSource';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { useOfflineFavoriteCountSync } from '~/composables/useOfflineFavoriteCountSync';
import { useOfflineFavorites } from '~/composables/useOfflineFavorites';

let isSyncingOfflineFavorites = false;

/**
 * Favorites saved before the snapshot was stored only carry ids. Fill them
 * from the API so wishlist_property_added can still go out; on failure the
 * entry goes through unchanged and the favorite is synced anyway.
 */
async function completeLegacyEntry(
	entry: OfflineFavoriteEntry,
	logger: ReturnType<typeof useLogger>
): Promise<OfflineFavoriteEntry> {
	if (entry.property && entry.commercialId) return entry;

	const fetched = await fetchPropertySnapshotSource(
		entry.commercialId ?? entry.propertyId
	);
	if (!fetched) {
		logger.warn('Offline favorite lookup failed, syncing without snapshot', {
			propertyId: entry.propertyId
		});
		return entry;
	}

	return {
		...entry,
		commercialId: entry.commercialId ?? fetched.commercialId,
		property: entry.property ?? fetched.property
	};
}

/**
 * Posts shorts favorites saved while logged out and emits
 * wishlist_property_added with the snapshot captured at favorite time, or
 * fetched by id for entries saved before the snapshot existed.
 * Safe to call from the shorts page and from the login plugin: a module
 * lock plus clearing the buffer first keeps concurrent flushers from
 * sending the same favorite twice.
 */
export async function syncOfflineFavorites(): Promise<void> {
	if (!import.meta.client || isSyncingOfflineFavorites) return;

	isSyncingOfflineFavorites = true;
	const logger = useLogger('OfflineFavoritesSync');
	let hasFlushedEntries = false;
	let offlineFavorites: ReturnType<typeof useOfflineFavorites> | null = null;
	let favoritesScope: ReturnType<typeof effectScope> | null = null;
	let entries: OfflineFavoriteEntry[] = [];
	let pendingIndex = 0;

	try {
		// Let the favorite's localStorage write flush before a second
		// useLocalStorage instance reads the buffer.
		await nextTick();

		// After the await there is no active effect scope. useLocalStorage
		// would keep a storage listener and its watchers until the tab closes.
		favoritesScope = effectScope();
		offlineFavorites = favoritesScope.run(() => useOfflineFavorites()) ?? null;
		if (!offlineFavorites) return;

		entries = offlineFavorites.getEntries();
		if (entries.length === 0) return;

		hasFlushedEntries = true;
		offlineFavorites.clearAll();

		const { useWishlists } =
			await import('~/composables/useWishlists/wishlists');
		// One parallel round so legacy lookups do not hold the lock N times over.
		// A later failure restores the completed entries, not the bare ids.
		entries = await Promise.all(
			entries.map(entry => completeLegacyEntry(entry, logger))
		);
		const ops = await useWishlists();

		for (; pendingIndex < entries.length; pendingIndex++) {
			const entry = entries[pendingIndex];
			if (!entry) continue;

			// Each entry carries the property from the short that was favorited.
			// The visible short at login time is a different property.
			const [result, error] = await ops.toggleWishlist(
				entry.propertyId,
				false,
				undefined,
				entry.property,
				entry.commercialId ?? null
			);

			if (error) {
				logger.error(
					`Failed to sync offline favorite: ${entry.propertyId}`,
					extractErrorLogContext(error)
				);
				offlineFavorites.addOfflineFavorite(
					entry.propertyId,
					entry.commercialId,
					entry.property
				);
			} else if (result) {
				ops.updateLocalWishlist(entry.propertyId, true);
			}
		}
	} catch (error) {
		// clearAll already ran. Put back anything not sent yet, including the
		// entry whose request threw. Return instead of rethrowing: the shorts
		// login watcher awaits this call without try/catch, and a rejection
		// before hydration makes Nuxt's app:error replace the page.
		const pending = entries.slice(pendingIndex);
		logger.error(
			`Failed to sync offline favorites: ${pending.length} still pending`,
			{
				pendingCount: pending.length,
				...extractErrorLogContext(error)
			}
		);
		if (offlineFavorites) {
			for (const entry of pending) {
				offlineFavorites.addOfflineFavorite(
					entry.propertyId,
					entry.commercialId,
					entry.property
				);
			}
		}
		return;
	} finally {
		if (favoritesScope) {
			// useLocalStorage writes on flush: 'pre'. Stop the scope only after
			// that tick, or clearAll and the restored entries never reach storage.
			await nextTick();
			favoritesScope.stop();
		}
		isSyncingOfflineFavorites = false;
		if (hasFlushedEntries) {
			useOfflineFavoriteCountSync().resetSessionCount();
		}
	}
}
